import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, animate } from "framer-motion";
import { Star, ArrowUpRight, CaretLeft, CaretRight } from "@phosphor-icons/react";
import api from "@/lib/api";

export function StatCounter({ value, suffix, label }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, value, {
      duration: 1.6, ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setN(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value]);
  return (
    <div ref={ref} data-testid={`stat-${label.toLowerCase().replace(/[^a-z]/g, "-")}`}>
      <div className="font-display text-5xl sm:text-6xl font-black tracking-tighter tabular-nums">
        {n}{suffix}
      </div>
      <div className="mt-2 text-sm text-zinc-500 font-medium">{label}</div>
    </div>
  );
}

export function Stars({ rating = 5, size = 16 }) {
  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} size={size} weight={i < rating ? "fill" : "regular"} className={i < rating ? "text-black" : "text-zinc-300"} />
      ))}
    </div>
  );
}

export function ServiceCard({ service }) {
  const Icon = service.icon;
  return (
    <Link
      to="/services"
      data-testid={`service-card-${service.slug}`}
      className={`group relative bg-white border border-black/10 p-6 h-full flex flex-col justify-between overflow-hidden hover:border-black transition-colors duration-300 ${service.span}`}
    >
      <div className="flex items-start justify-between">
        <Icon size={32} weight="duotone" className="text-black" />
        <ArrowUpRight size={20} className="text-zinc-300 group-hover:text-black group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-[color,transform] duration-300" />
      </div>
      <div className="mt-6">
        <h3 className="font-display text-xl font-bold tracking-tight">{service.title}</h3>
        <p className="mt-2 text-sm text-zinc-500 leading-relaxed max-w-md">{service.short}</p>
        <span className="mt-3 inline-block text-sm font-medium underline underline-offset-4 decoration-zinc-300 group-hover:decoration-black transition-colors">
          Learn More
        </span>
      </div>
    </Link>
  );
}

export function Testimonials({ dark = false }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get("/testimonials").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (items.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % items.length), 6000);
    return () => clearInterval(t);
  }, [items.length]);

  if (!items.length) return null;
  const t = items[idx];
  const sub = dark ? "text-zinc-400" : "text-zinc-500";
  const btn = dark ? "border-white/20 hover:bg-white hover:text-black" : "border-black/15 hover:bg-black hover:text-white";
  const dotActive = dark ? "bg-white" : "bg-black";
  const dotIdle = dark ? "bg-zinc-700" : "bg-zinc-300";

  return (
    <div data-testid="testimonials-carousel" className="relative">
      <motion.div
        key={t.name}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        className="max-w-4xl"
      >
        <div className="flex items-center gap-0.5">
          {Array.from({ length: 5 }).map((_, i) => (
            <Star key={i} size={22} weight={i < t.rating ? "fill" : "regular"} className={dark ? "text-white" : "text-black"} />
          ))}
        </div>
        <blockquote className="mt-8 font-display text-2xl sm:text-4xl font-medium tracking-tight leading-[1.2]">
          "{t.review}"
        </blockquote>
        <div className="mt-10 flex items-center gap-4">
          {t.photo && <img src={t.photo} alt={t.name} className="w-14 h-14 rounded-full object-cover grayscale" />}
          <div>
            <div className="font-semibold">{t.name}</div>
            <div className={`text-sm ${sub}`}>{t.business}</div>
          </div>
        </div>
      </motion.div>

      <div className="mt-12 flex items-center gap-4">
        <button
          data-testid="testimonial-prev"
          onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
          className={`w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${btn}`}
          aria-label="Previous"
        >
          <CaretLeft size={18} />
        </button>
        <button
          data-testid="testimonial-next"
          onClick={() => setIdx((i) => (i + 1) % items.length)}
          className={`w-11 h-11 rounded-full border flex items-center justify-center transition-colors ${btn}`}
          aria-label="Next"
        >
          <CaretRight size={18} />
        </button>
        <div className="flex gap-1.5 ml-2">
          {items.map((_, i) => (
            <span key={i} className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? `w-6 ${dotActive}` : `w-1.5 ${dotIdle}`}`} />
          ))}
        </div>
      </div>
    </div>
  );
}
